import {
  FirmwareApiV2FirmwareTypeGetRequest,
  FirmwareDTO,
  FirmwareType,
  ListWrapperFirmwareDTO,
} from "@generated/v2";
import { TableProps } from "antd/lib/table";
import { readerFirmwareGet } from "app/api/v2/readerFirmwareApi";
import Button from "app/components/Button/Button";
import Row from "app/components/Common/Row";
import TabPane from "app/components/Tabs/TabPane";
import useFetchPaginatedData from "app/hooks/useFetchPaginatedData";
import { ITabWithModalType } from "app/types/tabType";
import React, { memo, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styles from "./ReaderFirmwareTabPane.module.scss";
import { PRIVATE_ROUTES } from "app/routers/Router.config";
import ReaderFirmwareModal from "app/screens/ReaderFirmwareScreen/components/ReaderFirmwareModal";
import ErrorFallbackUI from "app/components/ErrorFallbackUI";
import ReaderFirmwareTable from "app/screens/ReaderFirmwareScreen/components/ReaderFirmwareTable";

interface IProps extends ITabWithModalType {
  firmwareType: FirmwareType;
  handleTableChange: TableProps<FirmwareDTO>["onChange"];
}

function ReaderFirmwareTabPane({
  activeTab,
  firmwareType,
  handleTableChange,
  modalConfig,
  redirect,
  ...rest
}: IProps): React.ReactElement {
  const { t } = useTranslation();

  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (activeTab) {
      setInitialized(true);
    }
  }, [activeTab]);

  /**
   * Get firmware version data
   */
  const {
    data: firmwareVersions,
    pagination,
    loading,
    refetch,
  } = useFetchPaginatedData<
    ListWrapperFirmwareDTO,
    FirmwareApiV2FirmwareTypeGetRequest
  >(readerFirmwareGet, {
    params: {
      pageSize: 10,
      type: firmwareType,
    },
    shouldCallApi: initialized,
  });

  const onAddFirmware = useCallback(
    () => redirect(PRIVATE_ROUTES.READER_FIRMWARE_CREATE_SCREEN.path),
    [redirect]
  );

  const onCloseModal = useCallback(
    () => redirect(PRIVATE_ROUTES.READER_FIRMWARE_SCREEN.path),
    [redirect]
  );

  return (
    <ErrorFallbackUI>
      <TabPane {...rest}>
        <Row align="middle" type="flex" justify="end">
          <Button
            className={styles.button}
            type="primary"
            size="large"
            onClick={onAddFirmware}
            icon="plus"
          >
            {t("readerFirmware.buttonUpload")}
          </Button>
        </Row>
        {!loading && (
          <ReaderFirmwareModal
            show={modalConfig && modalConfig.show ? modalConfig.show : false}
            firmwareType={firmwareType}
            close={onCloseModal}
            submit={refetch}
            key={`add-new-${firmwareType}`}
            initialized={initialized}
          />
        )}
        <ReaderFirmwareTable
          loading={loading}
          refetch={refetch}
          pagination={pagination}
          firmwareType={firmwareType}
          firmwareVersions={firmwareVersions}
          handleTableChange={handleTableChange}
        />
      </TabPane>
    </ErrorFallbackUI>
  );
}

export default memo(ReaderFirmwareTabPane);
